import type { OfficeItem } from '~/types/pages';
import type { PageTemplate } from '~/types/pages';

export type SchemaOrgNode = Record<string, unknown>;

export type SchemaKind =
  | 'home'
  | 'about'
  | 'contact'
  | 'search'
  | 'collection'
  | 'service-detail'
  | 'service-category'
  | 'promo'
  | 'offer'
  | 'article'
  | 'doctor'
  | 'doctors-list'
  | 'review'
  | 'reviews-hub'
  | 'news'
  | 'price'
  | 'web';

const WEB_ONLY_SLUGS = new Set([
  'info',
  'mavrina',
  'test-sobytiya-iz-zhizni-ao-stom',
  'privacy-policy',
  'polzovatelskoe-soglashenie',
  'soglasie-na-obrabotku-personalnyh-dannyh',
]);

export const withTrailingSlash = (path: string): string => {
  if (!path || path === '/') {
    return '/';
  }

  return path.endsWith('/') ? path : `${path}/`;
};

export const normalizeSiteUrl = (siteUrl: string): string => {
  const base = (siteUrl || '').trim().replace(/\/$/, '');

  if (!base) {
    return 'https://ao-stom.ru';
  }

  if (base.startsWith('http://') || base.startsWith('https://')) {
    return base;
  }

  return `https://${base}`;
};

export const absoluteUrl = (siteUrl: string, path: string): string => {
  const base = normalizeSiteUrl(siteUrl);

  if (!path || path === '/') {
    return `${base}/`;
  }

  const withSlash = path.startsWith('/') ? path : `/${path}`;

  return `${base}${withTrailingSlash(withSlash)}`;
};

export const organizationId = (siteUrl: string): string =>
  `${normalizeSiteUrl(siteUrl)}/#organization`;

export const websiteId = (siteUrl: string): string => `${normalizeSiteUrl(siteUrl)}/#website`;

export const webpageId = (pageUrl: string): string => `${pageUrl.replace(/\/$/, '')}/#webpage`;

export const schemaTelephone = (phone: string): string => {
  const digits = phone.replace(/[^\d+]/g, '');

  if (!digits) {
    return phone;
  }

  if (digits.startsWith('+')) {
    return digits;
  }

  if (digits.startsWith('8') && digits.length === 11) {
    return `+7${digits.slice(1)}`;
  }

  if (digits.startsWith('7') && digits.length === 11) {
    return `+${digits}`;
  }

  return `+${digits}`;
};

export const getOffices = (affiliates: unknown): OfficeItem[] => {
  if (!Array.isArray(affiliates) || affiliates.length === 0) {
    return [];
  }

  const first = affiliates[0] as Record<string, unknown>;

  if (first && typeof first.address === 'string') {
    return affiliates as OfficeItem[];
  }

  if (first && Array.isArray(first.offices)) {
    return (affiliates as { offices: OfficeItem[] }[]).flatMap(item => item.offices || []);
  }

  return [];
};

export const clinicId = (siteUrl: string, index: number): string =>
  `${normalizeSiteUrl(siteUrl)}/contacts/#clinic-${index + 1}`;

const isLegalOrInfo = (path: string): boolean => {
  const slug = withTrailingSlash(path).replace(/^\/|\/$/g, '');

  if (WEB_ONLY_SLUGS.has(slug)) {
    return true;
  }

  return /privacy|soglashenie|soglasie|personalnyh/.test(slug);
};

export const resolveSchemaKind = (path: string, template?: PageTemplate | string): SchemaKind => {
  const p = withTrailingSlash(path);

  if (p === '/') {
    return 'home';
  }

  if (p === '/about/') {
    return 'about';
  }

  if (p === '/contacts/') {
    return 'contact';
  }

  if (p === '/search/') {
    return 'search';
  }

  if (p === '/price/') {
    return 'price';
  }

  if (p === '/doctors/') {
    return 'doctors-list';
  }

  if (p.startsWith('/doctors/')) {
    return 'doctor';
  }

  if (p === '/action/') {
    return 'collection';
  }

  if (p.startsWith('/action/')) {
    return 'offer';
  }

  if (p === '/blog/') {
    return 'collection';
  }

  if (p.startsWith('/blog/')) {
    return 'article';
  }

  if (p === '/reviews/') {
    return 'reviews-hub';
  }

  if (p.includes('/otzyv-') || p.startsWith('/otzyv-')) {
    return 'review';
  }

  if (p === '/smi-o-nas/') {
    return 'collection';
  }

  if (p.startsWith('/smi-o-nas/')) {
    return 'news';
  }

  if (p === '/chek-ap/') {
    return 'collection';
  }

  if (p.startsWith('/chek-ap/')) {
    return 'service-detail';
  }

  if (p === '/services/') {
    return 'collection';
  }

  if (template === 'services-category') {
    return 'service-category';
  }

  if (template === 'services-detail') {
    return 'service-detail';
  }

  if (template === 'services-list' || template === 'doctors-category') {
    return 'collection';
  }

  if (template === 'page-list' || template === 'page-list-sidebar') {
    return 'collection';
  }

  if (isLegalOrInfo(p)) {
    return 'web';
  }

  if (template === 'page-detail') {
    const segments = p.split('/').filter(Boolean);

    if (segments.length === 1) {
      return 'promo';
    }

    return 'web';
  }

  return 'web';
};

export const webpageTypeByKind = (kind: SchemaKind): string => {
  switch (kind) {
    case 'about':
      return 'AboutPage';
    case 'contact':
      return 'ContactPage';
    case 'collection':
    case 'service-category':
    case 'doctors-list':
    case 'reviews-hub':
    case 'price':
      return 'CollectionPage';
    case 'service-detail':
    case 'promo':
      return 'MedicalWebPage';
    case 'doctor':
      return 'ProfilePage';
    default:
      return 'WebPage';
  }
};

export const collectionItemType = (path: string): string => {
  const p = withTrailingSlash(path);

  if (p.startsWith('/action/')) {
    return 'Offer';
  }

  if (p.startsWith('/blog/')) {
    return 'BlogPosting';
  }

  if (p.startsWith('/chek-ap/') || p.startsWith('/services/')) {
    return 'MedicalProcedure';
  }

  if (p.startsWith('/reviews/')) {
    return 'Review';
  }

  if (p.startsWith('/smi-o-nas/')) {
    return 'Article';
  }

  if (p.startsWith('/doctors/')) {
    return 'Physician';
  }

  return 'WebPage';
};

export const procedureTypeByPath = (path: string): 'SurgicalProcedure' | 'MedicalProcedure' => {
  if (/hirurg|udaleni|implant|sinus|operaciya|khirurg/i.test(path)) {
    return 'SurgicalProcedure';
  }

  return 'MedicalProcedure';
};

export const parseOpeningHours = (
  workingTime: string,
): { opens: string; closes: string } | null => {
  const match = workingTime?.match(/(\d{1,2}:\d{2}).*?(\d{1,2}:\d{2})/);

  if (!match?.[1] || !match[2]) {
    return null;
  }

  return { opens: match[1], closes: match[2] };
};
